import { useState, useEffect, useCallback } from 'react';
import Modal from './Modal';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toYMD(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

function getDaysInMonth(year, month) {
    // Returns array of Date objects for every day in the month
    const days = [];
    const total = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= total; d++) {
        days.push(new Date(year, month, d));
    }
    return days;
}

function isDateUnavailable(dateStr, ranges) {
    return ranges.some(
        (r) => dateStr >= r.start_date && dateStr <= r.end_date
    );
}

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

// ─── Calendar grid ─────────────────────────────────────────────────────────────

function CalendarGrid({ year, month, unavailableDates }) {
    const todayStr = toYMD(new Date());
    const days = getDaysInMonth(year, month);

    // Padding days before first day of month (0 = Sun)
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    const paddingBefore = Array.from({ length: firstDayOfWeek });

    return (
        <div className="avail-calendar__grid">
            {DAY_LABELS.map((label) => (
                <div key={label} className="avail-calendar__day-header">
                    {label}
                </div>
            ))}

            {paddingBefore.map((_, i) => (
                <div key={`pad-${i}`} className="avail-calendar__day avail-calendar__day--pad" />
            ))}

            {days.map((date) => {
                const dateStr = toYMD(date);
                const isUnavailable = isDateUnavailable(dateStr, unavailableDates);
                const isToday = dateStr === todayStr;

                let cls = 'avail-calendar__day';
                if (isUnavailable) cls += ' avail-calendar__day--unavailable';
                if (isToday)       cls += ' avail-calendar__day--today';

                return (
                    <div key={dateStr} className={cls} title={isUnavailable ? 'Unavailable' : ''}>
                        {date.getDate()}
                    </div>
                );
            })}
        </div>
    );
}

// ─── Main component ────────────────────────────────────────────────────────────

function AvailabilityCalendarModal({ restBase }) {
    const [isOpen, setIsOpen]               = useState(false);
    const [userId, setUserId]               = useState(null);
    const [userName, setUserName]           = useState('');
    const [unavailableDates, setUnavailableDates] = useState([]);
    const [loading, setLoading]             = useState(false);
    const [error, setError]                 = useState(null);
    const [currentMonth, setCurrentMonth]   = useState(new Date());

    // Listen for open event
    useEffect(() => {
        const handleOpen = (event) => {
            const { userId: uid, userName: uname } = event.detail;
            setUserId(uid);
            setUserName(uname);
            setCurrentMonth(new Date());
            setUnavailableDates([]);
            setError(null);
            setIsOpen(true);
        };

        window.addEventListener('openAvailabilityCalendar', handleOpen);
        return () => window.removeEventListener('openAvailabilityCalendar', handleOpen);
    }, []);

    // Fetch unavailable dates whenever the modal opens for a user
    useEffect(() => {
        if (!isOpen || !userId) return;

        let cancelled = false;
        setLoading(true);
        setError(null);

        const nonce  = window.gwBpGeoSearchAjax?.restNonce || '';
        const url    = `${restBase}/unavailable-dates/user/${userId}`;
        const headers = { 'Content-Type': 'application/json' };
        if (nonce) headers['X-WP-Nonce'] = nonce;

        fetch(url, { headers })
            .then((res) => {
                if (!res.ok) throw new Error(`Server responded with ${res.status}`);
                return res.json();
            })
            .then((data) => {
                if (!cancelled) {
                    setUnavailableDates(Array.isArray(data) ? data : []);
                    setLoading(false);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError('Could not load availability data. Please try again.');
                    setLoading(false);
                }
            });

        return () => { cancelled = true; };
    }, [isOpen, userId, restBase]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        setUserId(null);
        setUserName('');
        setUnavailableDates([]);
        setError(null);
    }, []);

    const prevMonth = () =>
        setCurrentMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
    const nextMonth = () =>
        setCurrentMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

    if (!isOpen) return null;

    const year  = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    return (
        <Modal
            title={`${userName}'s Availability`}
            onClose={handleClose}
            className="availability-calendar-modal"
        >
            <div className="avail-calendar">
                {/* Month navigation */}
                <div className="avail-calendar__nav">
                    <button
                        className="avail-calendar__nav-btn"
                        onClick={prevMonth}
                        aria-label="Previous month"
                    >
                        ‹ Prev
                    </button>
                    <span className="avail-calendar__month-label">
                        {MONTH_NAMES[month]} {year}
                    </span>
                    <button
                        className="avail-calendar__nav-btn"
                        onClick={nextMonth}
                        aria-label="Next month"
                    >
                        Next ›
                    </button>
                </div>

                {/* Legend */}
                <div className="avail-calendar__legend">
                    <span className="avail-calendar__legend-item avail-calendar__legend-item--unavailable">
                        Unavailable
                    </span>
                    <span className="avail-calendar__legend-item avail-calendar__legend-item--available">
                        Available
                    </span>
                </div>

                {/* Body */}
                {loading && (
                    <div className="avail-calendar__state">Loading…</div>
                )}
                {error && (
                    <div className="avail-calendar__state avail-calendar__state--error">{error}</div>
                )}
                {!loading && !error && (
                    <>
                        <CalendarGrid
                            year={year}
                            month={month}
                            unavailableDates={unavailableDates}
                        />
                        {unavailableDates.length === 0 && (
                            <p className="avail-calendar__empty">
                                No unavailable dates have been set.
                            </p>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
}

export default AvailabilityCalendarModal;
